import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Avatar, AvatarGroup } from '@components/avatar';
import * as Sentry from '@sentry/react';
import { LoaderXs } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import axios from 'axios';
import { detect } from 'detect-browser';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useFileSystem, useTldrawApp } from 'sn-wb-tldraw';
import { API } from 'src/api';
import { useFileStore } from 'src/features/files';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useAuth } from 'src/shared/hooks/use-auth';

import { useLocalStorage } from '../hooks/useLocalStorage';
import { useMultiplayerAssets } from '../hooks/useMultiplayerHook';
import { useWhiteboardService } from '../hooks/useWhiteboardService';
import ProgressBar from './ProgressBar';
import { Tldraw } from './Tldraw';

export const WhiteBoard = ({
  id,
  memberId,
  isPublic = false,
  isReadOnly = false,
}: {
  id: string;
  memberId: string;
  isPublic?: boolean;
  isReadOnly?: boolean;
}) => {
  const fileSystemEvents = useFileSystem();

  const { onAssetCreate, onAssetDelete } = useMultiplayerAssets();
  const {
    user,
    users,
    updateUserPoint,
    updateUserFollowing,
    isSyncing,
    updateSpotlightUser,
    spotLightUser,
    ...events
  } = useWhiteboardService(id, memberId);
  const followedUser = users.find((u) => u?.id === user?.following);
  const point = followedUser ? followedUser.point : [];
  const userDetails = useAuth((s) => s.userDetails);

  const activeUsers = useMemo(() => {
    !isPublic ? users?.filter((u) => u?.id !== user?.id) : [];
  }, [isPublic]);

  const spotlightJSX = (member) => (
    <div className="w-full">
      <div className="text-xs text-gray-400">{member} </div>

      {spotLightUser == user?.id || (
        <>
          <hr />
          <button
            className="cursor-pointer my-auto py-5 text-xss flex justify-around rounded-sm font-medium mt-6 mb-2 bg-blue-500 text-white w-full items-center"
            onClick={() => updateSpotlightUser(user?.id)}
          >
            Spotlight Me
          </button>
        </>
      )}
    </div>
  );
  const followJSX = (member, memberId) => (
    <div>
      <div className="text-xs text-gray-400">{member}</div>
      <hr />{' '}
      <span className="text-xs text-gray-200">
        {memberId === user?.following ? 'Following' : 'Follow'}
      </span>
    </div>
  );

  const handleUserFollowing = (memberId: string, point: number[]) => {
    if (memberId != user?.id) {
      if (memberId === user?.following) updateUserFollowing('', user.point);
      else updateUserFollowing(memberId, point);
    }
  };

  const handleError = async (error: Error, componentStack: string) => {
    if (process.env.NODE_ENV === 'production') {
      await axios.post('/api/error-report', {
        email: userDetails?.email,
        userId: userDetails?.userId,
        firstName: userDetails?.name,
        errorPage: window.location.href,
        device: detect(),
        errorMessage: error.message || 'No message available!',
        stackTrace: componentStack,
      });
    }
  };

  function handleFallback() {
    return <ErrorFallback id={id} />;
  }

  return (
    <Sentry.ErrorBoundary fallback={handleFallback} onError={handleError}>
      <div
        style={{
          position: 'relative',
          top: 0,
          left: 0,
          height: '100vh',
          marginTop: 'auto',
        }}
        id="whiteboard-block"
        className="object-contain overscroll-auto"
      >
        {!isPublic && (
          <div className="absolute z-100 right-20 top-8">
            <div className="flex justify-end gap-5">
              {
                <div className="flex gap-2 justify-center items-center">
                  {users?.map((member) => (
                    <div
                      className={` p-1 border-2 border-dotted border-yellow-600 rounded-full ${
                        +spotLightUser === member?.id ? '' : 'border-none'
                      }`}
                      key={member?.id}
                    >
                      <Tippy
                        placement="bottom"
                        interactive
                        appendTo={document.body}
                        key={member?.id}
                        content={
                          member?.id == user?.id
                            ? spotlightJSX(member?.user?.name)
                            : followJSX(member?.user?.name, member?.id)
                        }
                      >
                        <img
                          alt={member?.user?.name}
                          src={member?.user?.avatar}
                          className="h-24 w-24 rounded-full object-cover"
                          onClick={handleUserFollowing.bind(null, member?.id, point)}
                        />
                      </Tippy>
                    </div>
                  ))}
                </div>
              }
            </div>
          </div>
        )}

        {isSyncing ? (
          <div className="h-full w-full flex items-center justify-center m-auto">
            <div className="overflow-hidden">
              <LoaderXs className="mx-auto text-xss" />
            </div>
          </div>
        ) : (
          <div>
            <Tldraw
              autofocus
              showUI
              showTools={true}
              showZoom
              showMenu={false}
              showPages={false}
              showSponsorLink={false}
              showStyles
              readOnly={isPublic}
              onAssetCreate={onAssetCreate}
              onAssetDelete={onAssetDelete}
              updateUserPoint={updateUserPoint}
              updateUserFollowing={updateUserFollowing}
              updateSpotlightUser={updateSpotlightUser}
              spotLightUser={spotLightUser}
              activeUsers={activeUsers}
              user={user}
              users={users}
              {...fileSystemEvents}
              {...events}
            />
          </div>
        )}
      </div>
    </Sentry.ErrorBoundary>
  );
};

type ErrorFallbackProps = {
  id: string;
  onRetry?: () => void;
};

export function ErrorFallback({ id, onRetry }: ErrorFallbackProps) {
  const [hasError, setError] = useState<boolean>(true);
  const [isLoading, setLoading] = useState<boolean>(false); // new state variable
  const router = useRouter();
  const userDetails = useAuth((s) => s.userDetails);
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  const { clearDataForId, getDataFromLocalStorage } = useLocalStorage(); // Use your hook here

  const { isReadOnly } = useFileAccess();

  const handleRefreshClick = async () => {
    setLoading(true);
    if (onRetry) {
      onRetry();
    }
    const { shapes, assets, bindings, state, sections } = getDataFromLocalStorage(id);
    const payload = {
      id,
      shapes,
      assets,
      bindings,
      sections,
      state,
    };
    API.post('whiteboard/save', payload).finally(() => {
      setLoading(false);
      setError(false);
      clearDataForId(id);
    });
  };

  return (
    <>
      {hasError ? (
        <div
          style={{ height: '80vh' }}
          className=" w-full flex justify-center items-center overflow-hidden"
        >
          <div className="opacity-1 bg-white sticky w-575 text-center z-20 shadow-md border py-20 text-sm text-gray-600 rounded-sm">
            Hmm, we seem to have encountered an error. Please{' '}
            {isLoading ? (
              <span>loading...</span>
            ) : (
              <span onClick={handleRefreshClick} className="text-primary-500 cursor-pointer">
                refresh
              </span>
            )}{' '}
            the whiteboard!
          </div>
        </div>
      ) : (
        <WhiteBoard
          id={id}
          memberId={userDetails?.memberInformation.memberId || ''}
          isReadOnly={isReadOnly}
        />
      )}
    </>
  );
}
